import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { useJobPoll } from "@/components/crm/MassReassignDialog";

export const DeactivateSellerDialog = ({ user, preview, onClose, onDone }) => {
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const [summary, setSummary] = useState(null);
  const progress = useJobPoll(job, (d) => { setSummary(d); onDone(); });
  useEffect(() => { setJob(null); setSummary(null); }, [user]);

  const deactivate = async (redistribute) => {
    setBusy(true);
    try {
      await api.patch(`/admin/users/${user.id}`, { active: false });
      if (!redistribute) { toast.success("Usuario desactivado; sus leads se mantienen"); onDone(); onClose(); return; }
      const { data } = await api.post(`/admin/users/${user.id}/redistribute`);
      if (data.status === "running") setJob(data);
      else { setSummary(data); onDone(); }
    } catch (e) { toast.error(errorMessage(e)); onDone(); } finally { setBusy(false); }
  };
  const noTargets = preview && preview.other_active_sellers === 0;

  return (
    <Dialog open={Boolean(user)} onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid="deactivate-seller-dialog">
        <DialogHeader>
          <DialogTitle>Desactivar a {user?.name}</DialogTitle>
          <DialogDescription>Tiene {preview?.leads ?? 0} leads asignados.</DialogDescription>
        </DialogHeader>
        {summary ? (
          <p className="rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-800" data-testid="redistribute-summary">{summary.reassigned} leads repartidos entre los vendedores activos.</p>
        ) : job ? (
          <div className="space-y-2" data-testid="redistribute-progress">
            <Progress value={progress ? (progress.processed / progress.total) * 100 : 0} className="h-2" />
            <p className="text-sm text-slate-600">{progress?.processed || 0} / {job.total} procesados…</p>
          </div>
        ) : (
          <p className="text-sm text-slate-600">
            ¿Quieres repartir sus leads automáticamente (Round Robin entre vendedores activos) o mantenerlos asignados?
            {noTargets && <span className="mt-2 block rounded-xl bg-amber-50 px-3 py-2 text-amber-800" data-testid="redistribute-no-sellers">No hay vendedores activos para repartir</span>}
          </p>
        )}
        <DialogFooter className="gap-2">
          {summary || job ? (
            <Button className="rounded-xl" disabled={job && !summary} onClick={onClose} data-testid="deactivate-close">Cerrar</Button>
          ) : (
            <>
              <Button variant="outline" className="rounded-xl" disabled={busy} onClick={() => deactivate(false)} data-testid="deactivate-keep-button">Mantener asignados</Button>
              <Button className="rounded-xl bg-[#1E6FD9]" disabled={busy || noTargets} onClick={() => deactivate(true)} data-testid="deactivate-redistribute-button">Repartir leads</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
